import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { WorkflowCard, WorkflowCardVariant } from 'components/molecules/Generic/WorkflowCard';
import { ICONS, resolveWorkflowNavigationEndpoint } from 'utils/ppaHelpers';
import {
    ppaWorkflowBtnTestId,
    ppaWorkflowCardTestId,
    slugifyTestIdSegment,
} from 'constants/ppaTestIds';

export interface WorkflowCardData {
    workflow_id?: string;
    title: string;
    action_button?: {
        icon?: string;
        label?: string;
        iconVariant?: WorkflowCardVariant;
        iconColor?: string;
        endpoint?: string;
        isDisabled?: boolean;
    },
    description_points: string[];
}

interface WorkflowCardsGridProps {
    cards: WorkflowCardData[];
    style?: React.CSSProperties;
    className?: string;
    'data-testid'?: string;
}

export const WorkflowCardsGrid: FC<WorkflowCardsGridProps> = ({
    cards,
    style,
    className = '',
    'data-testid': testId,
}) => {
    const navigate = useNavigate();
    return (
        <div
            className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${className}`}
            style={style}
            data-testid={testId}
        >
            {cards.map((card, i) => {
                const actionButton = card.action_button;
                const endpoint = actionButton
                    ? resolveWorkflowNavigationEndpoint(card) ?? actionButton.endpoint
                    : undefined;
                const iconName = actionButton?.icon;
                const workflowKey =
                    card.workflow_id ?? slugifyTestIdSegment(card.title) ?? String(i);

                return (
                    <div key={card.workflow_id || i} className="min-w-0">
                        <WorkflowCard
                            title={card.title}
                            descriptionPoints={card.description_points}
                            buttonLabel={actionButton?.label ?? ''}
                            onButtonClick={
                                endpoint
                                    ? () => navigate(
                                        endpoint.startsWith('/')
                                            ? endpoint
                                            : `/${endpoint}`,
                                    )
                                    : undefined
                            }
                            icon={
                                iconName && ICONS[iconName]
                                    ? React.createElement(ICONS[iconName], { color: actionButton?.iconColor })
                                    : undefined
                            }
                            iconVariant={actionButton?.iconVariant ?? 'warning'}
                            isDisabled={actionButton?.isDisabled ?? false}
                            data-testid={ppaWorkflowCardTestId(workflowKey)}
                            buttonTestId={ppaWorkflowBtnTestId(workflowKey)}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default WorkflowCardsGrid;
